import React, { ReactNode, useCallback, useMemo } from 'react';
import { useAvContext } from '@context/AvContextProvider';
import { SoarApp } from '../soar/routePaths';
import { SoarProvider, SoarUser } from '../soar/SoarContext';

/**
 * Project-specific wrapper that automatically fetches accountId, user,
 * and refresh logic from the application's global AvContext.
 */
export const ProjectSocAutomationProvider = ({
  children,
  soarApp = 'threats',
  entityScope,
}: {
  children: ReactNode;
  soarApp?: SoarApp;
  entityScope?: string[];
}) => {
  const { accountId, accessToken, user, userPermissions, featureFlags, refreshAccessToken } = useAvContext();

  const soarUser: SoarUser | null = useMemo(() => {
    if (!user) return null;
    return {
      userId: user.userId,
      userName: user.userName,
      email: user.email,
      permissions: userPermissions, // Pass the structured permissions
      featureFlags, // Pass the active feature flags
    };
  }, [user, userPermissions, featureFlags]);

  /**
   * Wrapper for the host's refreshAccessToken function.
   * This callback is passed to the portable SoarProvider to allow
   * the component library to trigger a refresh via the zfab host.
   */
  const handleRefresh = useCallback(() => refreshAccessToken(accountId), [refreshAccessToken, accountId]);

  return (
    <SoarProvider
      accountId={accountId}
      accessToken={accessToken}
      user={soarUser}
      onRefresh={handleRefresh}
      soarApp={soarApp}
      entityScope={entityScope}>
      {children}
    </SoarProvider>
  );
};
